/**
 * Data layer for `braze-iterable-customer-io-integrations`.
 * Thin typed fetch wrappers over /api/v1/braze-iterable-customer-io-integrations.
 * Raw `fetch()` is confined to this file per the dash contract.
 *
 * Backend shape:
 *   POST   /api/v1/braze-iterable-customer-io-integrations          body: {provider, api_key, workspace}
 *   GET    /api/v1/braze-iterable-customer-io-integrations          -> {items: Config[]}
 *   GET    /api/v1/braze-iterable-customer-io-integrations/:id      -> Config
 *   DELETE /api/v1/braze-iterable-customer-io-integrations/:id      -> {ok, disabled}
 *   POST   /api/v1/braze-iterable-customer-io-integrations/:id/events body: {external_event_id, event_name, user_email?}
 *   GET    /api/v1/braze-iterable-customer-io-integrations/:id/events -> {items: EventRow[]}
 */

export type Vendor = "braze" | "iterable" | "customer_io";

export interface IntegrationConfig {
	id: string;
	provider: Vendor;
	workspace: string;
	enabled: boolean;
	created_at: number;
}

export interface IntegrationEventRow {
	id: string;
	external_event_id: string | null;
	event_name: string | null;
	status: string;
	created_at: number;
}

export interface ListConfigsResponse {
	items: IntegrationConfig[];
}

export interface ListEventsResponse {
	items: IntegrationEventRow[];
}

export interface CreateConfigBody {
	provider: Vendor;
	api_key: string;
	workspace: string;
}

export interface CreateEventBody {
	external_event_id: string;
	event_name: string;
	user_email?: string;
}

export interface CreateEventResponse {
	ok: boolean;
	deduped: boolean;
	event_id: string;
	created_at?: number;
}

const BASE = "/api/v1/braze-iterable-customer-io-integrations";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listConfigs(): Promise<ListConfigsResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListConfigsResponse>(res);
}

export async function createConfig(body: CreateConfigBody): Promise<IntegrationConfig> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<IntegrationConfig>(res);
}

export async function deleteConfig(id: string): Promise<{ ok: boolean; disabled: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ ok: boolean; disabled: boolean }>(res);
}

export async function listEvents(configId: string): Promise<ListEventsResponse> {
	const res = await fetch(`${BASE}/${encodeURIComponent(configId)}/events`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListEventsResponse>(res);
}

export async function createEvent(
	configId: string,
	body: CreateEventBody,
): Promise<CreateEventResponse> {
	const res = await fetch(`${BASE}/${encodeURIComponent(configId)}/events`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateEventResponse>(res);
}

export const brazeIterableCustomerIoIntegrationsQueryKeys = {
	all: ["braze-iterable-customer-io-integrations"] as const,
	list: () => [...brazeIterableCustomerIoIntegrationsQueryKeys.all] as const,
	detail: (id: string) =>
		[...brazeIterableCustomerIoIntegrationsQueryKeys.all, id] as const,
	events: (configId: string) =>
		[...brazeIterableCustomerIoIntegrationsQueryKeys.all, configId, "events"] as const,
};
